import { useEffect } from 'react'
import { useLivekitStore } from '../stores/livekitStore'
import { useVoiceStore } from '../stores/voiceStore'
import { useDmCallStore } from '@os/messenger/web/stores/dmCallStore'
import { mediaManager } from '../services/mediaManager'

/**
 * Privacy gate for the DM↔Channel bridge feature.
 *
 * When the user is in an audio-channel AND a DM call connects (or
 * vice versa), automatically mute the channel mic to close the primary
 * acoustic leak path:
 *
 *   DM peer's voice → my speakers → my channel mic → channel listeners
 *
 * The save-slot (`voiceStore.savedChannelMicEnabledBeforeDm`) records
 * the channel mic state at the moment we entered combined state. On
 * exit (either room disconnects) we set channel mic back to that value
 * via the same path that drives the mute button. If the user manually
 * toggled mic during the combined state, the saved value still applies
 * — they only set their *active session* preference, not their
 * default state.
 *
 * Scope of v1: mic-mute only. Channel audio PLAYBACK is NOT muted —
 * that would require touching LiveKitBackgroundAudio (critical voice
 * path) and is a future MR. Residual leak (channel speaker → my DM
 * mic → F) is documented in the spec.
 *
 * Bridge (separate MR) keys off the same combined-state signal but
 * with the gating rule "bridge active iff both mics ON" — so by default
 * (channel mic auto-muted) bridge is OFF. The user un-mutes channel
 * mic to opt INTO bridge.
 */
export function useDmChannelPrivacyGate(): void {
  useEffect(() => {
    let lastBoth = false

    const evaluate = (): void => {
      const channelRoom = useLivekitStore.getState().room
      const dmRoom = useDmCallStore.getState().room
      const both = channelRoom !== null && dmRoom !== null

      if (both && !lastBoth) {
        // Entering combined state. Save current channel mic state, then
        // auto-mute. Reading the channel room ref fresh (the closure
        // already captured it above, but we use it once and discard).
        const channelMicOn = channelRoom.localParticipant.isMicrophoneEnabled
        useVoiceStore.getState().setSavedChannelMicEnabledBeforeDm(channelMicOn)
        if (channelMicOn) {
          mediaManager
            .forRoom(channelRoom)
            .setMicrophoneEnabled(false)
            .catch((err) => {
              console.error('[dmChannelPrivacyGate] failed to auto-mute channel mic:', err)
            })
          console.info('[dmChannelPrivacyGate] auto-mute on combined-state enter', {
            savedChannelMicOn: channelMicOn,
          })
        } else {
          // Channel mic was already off — nothing to mute, but save
          // anyway so restore writes the right value.
          console.info('[dmChannelPrivacyGate] combined-state enter, channel mic already off')
        }
      } else if (!both && lastBoth) {
        // Exiting combined state. Restore channel mic to saved value
        // IF we still have a channel room (i.e. DM ended, channel
        // remains). If channel itself ended, no restore needed —
        // the save-slot is just cleared.
        const saved = useVoiceStore.getState().savedChannelMicEnabledBeforeDm
        if (channelRoom !== null && saved !== null) {
          mediaManager
            .forRoom(channelRoom)
            .setMicrophoneEnabled(saved)
            .catch((err) => {
              console.error('[dmChannelPrivacyGate] failed to restore channel mic:', err)
            })
          console.info('[dmChannelPrivacyGate] restore on combined-state exit', {
            restoredTo: saved,
          })
        }
        useVoiceStore.getState().setSavedChannelMicEnabledBeforeDm(null)
      }
      lastBoth = both
    }

    // Initial evaluation on mount (covers the case where both rooms
    // were already present when this component mounted — e.g. HMR).
    evaluate()

    // Subscribe to both stores' room field. Zustand's subscribe runs
    // the listener whenever any state changes; we re-check every time
    // because cross-store reads aren't reactive otherwise. Cheap (one
    // ref compare + a Boolean) so churn is fine.
    const unsubLivekit = useLivekitStore.subscribe(evaluate)
    const unsubDm = useDmCallStore.subscribe(evaluate)

    return () => {
      unsubLivekit()
      unsubDm()
    }
  }, [])
}
